import { Injectable } from '@angular/core';

import { environment } from '../../../environments/environment';
import { resolveApiBaseUrl } from '@core/api/api-base-url';
import { sanitizeRoute } from './sanitize-route';

export type ClientLogLevel = 'error' | 'warn' | 'info';

export interface ClientViewport {
  width: number;
  height: number;
  visualWidth: number | null;
  visualHeight: number | null;
  dpr: number;
  scrollY: number;
}

interface ClientLogEvent {
  level: ClientLogLevel;
  kind: string;
  message: string;
  route: string;
  at: string;
  requestId?: string | null;
  status?: number;
  apiPath?: string;
  viewport: ClientViewport;
  userAgent: string;
}

const MAX_QUEUE = 50;
const MAX_BATCH = 20;
const INFO_FLUSH_MS = 5000;
const ERROR_FLUSH_MS = 500;

/**
 * Development-only browser trail: errors, failed API calls, and route changes,
 * sent to the backend log. Uses fetch, not HttpClient, so a failed report
 * cannot report itself.
 */
@Injectable({ providedIn: 'root' })
export class ClientDiagnosticsService {
  readonly enabled = !environment.production && typeof window !== 'undefined';

  private readonly endpoint = `${resolveApiBaseUrl(
    globalThis.location?.hostname ?? 'localhost',
    environment.apiBaseUrl,
    globalThis.location?.protocol ?? 'http:'
  )}/api/client-logs`;
  private queue: ClientLogEvent[] = [];
  private timer: ReturnType<typeof setTimeout> | null = null;
  private listening = false;

  /** Hooks uncaught errors and page hide. Call once at startup. */
  start(): void {
    if (!this.enabled || this.listening) return;
    this.listening = true;

    window.addEventListener('error', (event) => {
      this.error('window-error', event.error ?? event.message);
    });
    window.addEventListener('unhandledrejection', (event) => {
      this.error('unhandled-rejection', event.reason);
    });
    window.addEventListener('pagehide', () => this.flush(true));
    document.addEventListener('visibilitychange', () => {
      if (document.visibilityState === 'hidden') this.flush(true);
    });
  }

  error(kind: string, error: unknown): void {
    this.push({ level: 'error', kind, message: describeError(error) });
  }

  info(kind: string, message: string): void {
    this.push({ level: 'info', kind, message });
  }

  httpFailure(status: number, url: string, requestId: string | null, method: string): void {
    this.push({
      level: status === 0 || status >= 500 ? 'error' : 'warn',
      kind: 'http',
      message: status === 0 ? `${method} network error` : `${method} failed`,
      status,
      apiPath: apiPathOf(url),
      requestId
    });
  }

  currentRoute(): string {
    return sanitizeRoute(`${location.pathname}${location.search}`);
  }

  viewport(): ClientViewport {
    return {
      width: window.innerWidth,
      height: window.innerHeight,
      visualWidth: window.visualViewport ? Math.round(window.visualViewport.width) : null,
      visualHeight: window.visualViewport ? Math.round(window.visualViewport.height) : null,
      dpr: window.devicePixelRatio,
      scrollY: Math.round(window.scrollY)
    };
  }

  private push(partial: Pick<ClientLogEvent, 'level' | 'kind' | 'message'> & Partial<ClientLogEvent>): void {
    if (!this.enabled) return;
    this.queue.push({
      route: this.currentRoute(),
      at: new Date().toISOString(),
      viewport: this.viewport(),
      userAgent: navigator.userAgent.slice(0, 160),
      ...partial
    });
    if (this.queue.length > MAX_QUEUE) this.queue.splice(0, this.queue.length - MAX_QUEUE);
    this.schedule(partial.level === 'info' ? INFO_FLUSH_MS : ERROR_FLUSH_MS);
  }

  private schedule(delayMs: number): void {
    if (this.timer && delayMs >= INFO_FLUSH_MS) return;
    if (this.timer) clearTimeout(this.timer);
    this.timer = setTimeout(() => this.flush(false), delayMs);
  }

  private flush(leaving: boolean): void {
    if (this.timer) {
      clearTimeout(this.timer);
      this.timer = null;
    }
    while (this.queue.length) {
      const events = this.queue.splice(0, MAX_BATCH);
      void fetch(this.endpoint, {
        method: 'POST',
        headers: { 'Content-Type': 'application/json' },
        body: JSON.stringify({ events }),
        credentials: 'include',
        keepalive: leaving
      }).catch(() => undefined);
    }
  }
}

function describeError(error: unknown): string {
  if (error instanceof Error) {
    const frames = (error.stack ?? '')
      .split('\n')
      .slice(1, 4)
      .map((line) => line.trim())
      .join(' | ');
    return `${error.name}: ${error.message}${frames ? ` @ ${frames}` : ''}`;
  }
  if (typeof error === 'string') return error;
  try {
    return JSON.stringify(error);
  } catch {
    return String(error);
  }
}

function apiPathOf(url: string): string {
  try {
    const parsed = new URL(url, location.href);
    return sanitizeRoute(`${parsed.pathname}${parsed.search}`);
  } catch {
    return sanitizeRoute(url);
  }
}
